"use client";

import { cn } from "@/utils/cn";
import { CalendarIcon } from "lucide-react";
import {
	DatePicker as AriaDatePicker,
	DateRangePicker as AriaDateRangePicker,
	DatePickerProps,
	DateRangePickerProps,
	DateValue,
	Dialog,
	Text,
	ValidationResult,
} from "react-aria-components";

import { Button } from "./button";
import {
	CalendarCell,
	CalendarGrid,
	CalendarGridBody,
	CalendarGridHeader,
	CalendarHeaderCell,
	CalendarHeading,
	JollyCalendar,
	RangeCalendar,
} from "./calendar";
import { DateInput, DateSegment } from "./datefield";
import { FieldGroup, Label } from "./field";
import { Popover } from "./popover";

interface JollyDatePickerProps<T extends DateValue> extends Omit<DatePickerProps<T>, "granularity"> {
	label?: string;
	description?: string;
	errorMessage?: string | ((validation: ValidationResult) => string);
	className?: string;
	contentClassName?: string;
	granularity?: "year" | "month" | "day";
}

function JollyDatePicker<T extends DateValue>({
	label,
	description,
	errorMessage,
	className,
	contentClassName,
	granularity,
	...props
}: JollyDatePickerProps<T>) {
	const mode = granularity === "year" ? "year" : granularity === "month" ? "month" : "day";

	return (
		<AriaDatePicker {...props} granularity={granularity as any} className={cn("group flex flex-col gap-1", className)}>
			{label && <Label className="text-title-medium mb-2">{label}</Label>}

			<FieldGroup>
				<DateInput className="flex-1 px-2">
					{segment => {
						if (granularity === "month" && segment.type !== "month") return <></>;
						if (granularity === "year" && segment.type !== "year") return <></>;
						return (
							<DateSegment segment={segment} className={cn("unfocusable", segment.isPlaceholder && "uppercase")} />
						);
					}}
				</DateInput>
				<Button
					variant="ghost"
					size="icon"
					className="mr-1 size-6 data-[focus-visible]:ring-0 data-[focus-visible]:ring-offset-0"
				>
					<CalendarIcon aria-hidden className="size-4" />
				</Button>
			</FieldGroup>

			{description && (
				<Text slot="description" className="text-body-small">
					{description}
				</Text>
			)}

			<Text slot="errorMessage" className="text-body-small-medium text-destructive h-4">
				{typeof errorMessage === "string" ? errorMessage : ""}
			</Text>

			<Popover placement="bottom start">
				<Dialog className={cn("p-4", contentClassName)}>
					<JollyCalendar mode={mode} />
				</Dialog>
			</Popover>
		</AriaDatePicker>
	);
}

interface JollyDateRangePickerProps<T extends DateValue> extends DateRangePickerProps<T> {
	label?: string;
	description?: string;
	errorMessage?: string | ((validation: ValidationResult) => string);
	className?: string;
	contentClassName?: string;
}

function JollyDateRangePicker<T extends DateValue>({
	label,
	description,
	errorMessage,
	className,
	contentClassName,
	...props
}: JollyDateRangePickerProps<T>) {
	return (
		<AriaDateRangePicker {...props} className={cn("group flex flex-col gap-1", className)}>
			{label && <Label className="text-title-medium">{label}</Label>}

			<FieldGroup className="flex items-center justify-between">
				<div className="flex items-center gap-1">
					<DateInput slot="start" className="px-2">
						{segment => <DateSegment segment={segment} />}
					</DateInput>
					<span aria-hidden="true" className="text-muted-foreground">
						–
					</span>
					<DateInput slot="end" className="flex-1 px-2">
						{segment => <DateSegment segment={segment} />}
					</DateInput>
				</div>

				<Button
					variant="ghost"
					size="icon"
					className="mr-1 size-6 data-[focus-visible]:ring-0 data-[focus-visible]:ring-offset-0"
				>
					<CalendarIcon aria-hidden className="size-4" />
				</Button>
			</FieldGroup>

			{description && (
				<Text slot="description" className="text-body-small">
					{description}
				</Text>
			)}

			<Text slot="errorMessage" className="text-body-small-medium text-destructive h-4">
				{typeof errorMessage === "string" ? errorMessage : ""}
			</Text>

			<Popover placement="bottom start" className="w-auto">
				<Dialog className={cn("p-4 outline-none", contentClassName)}>
					<RangeCalendar>
						<CalendarHeading mode="day" />
						<CalendarGrid>
							<CalendarGridHeader>{day => <CalendarHeaderCell>{day}</CalendarHeaderCell>}</CalendarGridHeader>
							<CalendarGridBody>{date => <CalendarCell date={date} />}</CalendarGridBody>
						</CalendarGrid>
					</RangeCalendar>
				</Dialog>
			</Popover>
		</AriaDateRangePicker>
	);
}

export { JollyDatePicker, JollyDateRangePicker };
export type { JollyDatePickerProps, JollyDateRangePickerProps };
